import React from 'react';
import { Calendar, MapPin, ExternalLink, Ticket, Star } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { UPCOMING_EVENTS, getUpcomingEvents } from '../data/upcomingEvents';

const Events = () => {
  // Show only future events on the cards grid (past events auto-drop).
  const upcomingEvents = getUpcomingEvents();

  // JSON-LD Event entries are generated from the same data — no drift.
  const eventSchemas = UPCOMING_EVENTS.map((e) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `${e.organiser} — ${e.title} (${e.city})`,
    description: e.description,
    startDate: e.startDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: e.city,
      address: {
        '@type': 'PostalAddress',
        addressLocality: e.city,
        addressCountry: 'GB',
      },
    },
    offers: {
      '@type': 'Offer',
      url: e.bookingUrl,
      availability: 'https://schema.org/InStock',
      priceCurrency: 'GBP',
    },
    organizer: { '@type': 'Organization', name: e.organiser, url: e.organiserUrl },
    image: e.image,
    about: { '@type': 'Person', name: 'Michael Jackson', alternateName: 'King of Pop' },
  }));

  return (
    <div className="min-h-screen pt-20">
      {/* SEO Meta Tags - Events Page */}
      <Helmet>
        <title>Michael Jackson Fan Events 2026 — The MJ Experience London | PopCrown</title>
        <meta name="description" content="Michael Jackson fan events 2026. Featuring The MJ Experience London — immersive MJ workshops, Sip & Paint nights and Rhinestone Glove making. Plus curated tribute concerts, fan meet-ups and special celebrations worldwide." />
        <meta name="keywords" content="Michael Jackson events 2026, MJ fan events, Michael Jackson tribute events, King of Pop events, MJ meet and greet, PopCrown events, Michael Jackson movie events, The MJ Experience, MJ Sip and Paint London, Michael Jackson rhinestone glove workshop, MJ themed art night London, immersive Michael Jackson events" />
        <link rel="canonical" href="https://popcrownofficial.com/events" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://popcrownofficial.com/events" />
        <meta property="og:title" content="Michael Jackson Fan Events 2026 — The MJ Experience London" />
        <meta property="og:description" content="Featured member The MJ Experience London — immersive MJ workshops, Sip & Paint nights, Rhinestone Glove making. Plus curated MJ tribute events worldwide." />
        <meta property="og:image" content="https://static.wixstatic.com/media/34f369_7fa6fa45490340ed84ada12a844066f2~mv2.jpg/v1/fill/w_1380,h_467,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/The%20MJ%20Experience%20Sip%20and%20Paint%20_edited.jpg" />
        <meta property="og:site_name" content="PopCrown Official" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Michael Jackson Fan Events 2026 — The MJ Experience London" />
        <meta name="twitter:description" content="The MJ Experience London — immersive MJ workshops, Sip & Paint nights and Rhinestone Glove making. Featured PopCrown member." />
        <meta name="twitter:image" content="https://static.wixstatic.com/media/34f369_7fa6fa45490340ed84ada12a844066f2~mv2.jpg/v1/fill/w_1380,h_467,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/The%20MJ%20Experience%20Sip%20and%20Paint%20_edited.jpg" />
        <script type="application/ld+json">{JSON.stringify([
          ...eventSchemas,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com/" },
              { "@type": "ListItem", "position": 2, "name": "Events", "item": "https://popcrownofficial.com/events" }
            ]
          }
        ])}</script>
      </Helmet>

      {/* Schema.org JSON-LD for Events Page Image - SEO */}
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "ImageObject",
        "name": "Thriller Live Musical London West End - Michael Jackson Tribute Show",
        "description": "Thriller Live musical billboard at London's West End Lyric Theatre on Shaftesbury Avenue featuring songs of Michael Jackson and the Jackson 5 including Thriller, Beat It, Billie Jean, I Want You Back, ABC, and more iconic hits from the King of Pop",
        "contentUrl": "https://popcrownofficial.com/hero_events.jpg",
        "creditText": "Photo by Panicos Georgiou",
        "creator": {
          "@type": "Person",
          "name": "Panicos Georgiou"
        },
        "copyrightNotice": "Free to use via Pexels",
        "acquireLicensePage": "https://www.pexels.com/photo/people-walking-on-the-street-13164645/",
        "keywords": "Thriller Live, Michael Jackson musical, London West End, Lyric Theatre, MJ tribute show, Jackson 5 songs, King of Pop, Shaftesbury Avenue, theatre district, MJ fan events, Michael Jackson concert, Thriller musical"
      }} />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image - London West End Thriller Live - Maximum SEO */}
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(/hero_events.jpg)',
            backgroundPosition: 'left center',
          }}
          role="img"
          aria-label="Thriller Live musical show billboard at London West End Lyric Theatre celebrating Michael Jackson King of Pop and Jackson 5 greatest hits - iconic MJ tribute performance featuring Thriller Beat It Billie Jean - Photo by Panicos Georgiou via Pexels"
        />
        {/* Hidden img tag for SEO crawlers - Maximum keyword optimization */}
        <img 
          src="/hero_events.jpg" 
          alt="Thriller Live musical London West End Lyric Theatre Shaftesbury Avenue - Michael Jackson tribute show billboard featuring songs of MJ King of Pop and The Jackson 5 including Thriller Beat It Billie Jean Smooth Criminal Bad I Want You Back ABC Ill Be There Can You Feel It - iconic theatre district venue celebrating the greatest entertainer of all time - fan events and MJ tribute concerts worldwide"
          className="sr-only"
          width="1920"
          height="717"
          loading="eager"
        />
        {/* Overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(225, 6, 0, 0.3) 100%)'
        }} />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 text-[#E10600] mx-auto mb-6 crown-pulse object-contain" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="thriller-text">Michael Jackson Fan Events 2026</span>
          </h1>
          <p className="text-xl text-white mb-4 max-w-2xl mx-auto">
            Michael Jackson fan events, conventions and tribute celebrations
          </p>
          <p className="text-sm text-gray-300 mb-8">
            Not affiliated with any artist estates or rights holders
          </p>
          <div className="mx-auto h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
        </div>
      </section>

      {/* Photo Credit */}
      <div className="bg-black py-2">
        <p className="text-xs text-gray-400 text-center">
          Hero image: "Thriller Live Musical - London West End Lyric Theatre" by{' '}
          <a 
            href="https://www.pexels.com/@panicos-georgiou-290424152/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-[#E10600] transition-colors underline"
          >
            Panicos Georgiou
          </a>
          {' '}via{' '}
          <a 
            href="https://www.pexels.com/photo/people-walking-on-the-street-13164645/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-[#E10600] transition-colors underline"
          >
            Pexels
          </a>
          {' '}(Free to use)
        </p>
      </div>

      {/* Kingvention — DANGEROUS The Party (Early Bird + PopCrown Promo) */}
      <section className="py-16" style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #2d1a1a 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 px-5 py-2 bg-[#E10600] text-black rounded-full text-sm font-bold uppercase tracking-wider mb-5" data-testid="kingvention-promo-badge">
                <Star className="w-4 h-4" />
                Kingvention Presents — Early Bird Open
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">
                <span className="thriller-text">DANGEROUS: The Party</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-300 mb-2">
                Celebrating 35 years of Dangerous · Saturday 14 November 2026 · 7:30pm – 2am
              </p>
              <p className="text-base text-gray-400">
                Notting Hill Arts Club, 21 Notting Hill Gate, London W11 3JQ · 18+
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden" style={{
              background: 'linear-gradient(135deg, #0f0f0f 0%, #1a0000 100%)',
              border: '2px solid #E10600',
              boxShadow: '0 0 40px rgba(225, 6, 0, 0.25)'
            }} data-testid="kingvention-promo-card">
              {/* Bundle Hero Image — Official Dangerous T-shirt (front & back) + Party Ticket */}
              <div className="bg-white flex items-center justify-center p-4 md:p-6">
                <img
                  src="/events/kingvention-dangerous-bundle.jpg"
                  alt="PopCrown Exclusive Early Bird Bundle — Official Michael Jackson Dangerous 35th Anniversary T-shirt front and back plus DANGEROUS: The Party ticket for Saturday 14 November 2026 at Notting Hill Arts Club, London"
                  className="w-full max-w-2xl h-auto"
                  loading="lazy"
                  data-testid="kingvention-bundle-image"
                />
              </div>

              <div className="p-6 md:p-10">
                <p className="text-base md:text-lg text-gray-200 mb-5 leading-relaxed">
                  Celebrating 35 years of <em>Dangerous</em> at the legendary Notting Hill Arts Club — the venue that has hosted Amy Winehouse, Ed Sheeran and Bruno Mars, and welcomed Beyoncé and Jay-Z. DJ Wiz spinning the biggest hits from <em>Dangerous</em> plus fan favourites and deep cuts. Special Michael Jackson-themed cocktails (Black or White, JAM, Dangerous — from £13). Over 50% of tickets already gone.
                </p>

                <div className="rounded-xl p-5 md:p-6 mb-6" style={{
                  background: 'rgba(225, 6, 0, 0.08)',
                  border: '1px solid rgba(225, 6, 0, 0.35)'
                }}>
                  <h3 className="text-xl md:text-2xl font-bold text-[#E10600] mb-2 uppercase tracking-wide">
                    PopCrown Members — Early Bird Bundle
                  </h3>
                  <p className="text-base md:text-lg text-gray-200 mb-2 leading-relaxed">
                    Early Bird tickets available <strong className="text-white">14–27 July 2026</strong>. PopCrown members get exclusive access to the <strong className="text-white">Early Bird Bundle (party ticket + official T-shirt)</strong> — sign up free or paid to receive your member code.
                  </p>
                  <p className="text-sm text-gray-400">
                    Full-price tickets go live 28 July. 18+ venue. No door sales. 13–17 with prior approval via info@kingvention.com.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 justify-center">
                  <a
                    href="https://kingvention.simpletix.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm md:text-base font-bold transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)', color: '#fff' }}
                    data-testid="kingvention-book-btn"
                  >
                    <Ticket className="w-4 h-4" />
                    Book Early Bird Tickets
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <a
                    href="/join"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm md:text-base font-bold bg-black text-[#E10600] border-2 border-[#E10600] transition-all hover:bg-[#E10600] hover:text-black"
                    data-testid="kingvention-join-popcrown-btn"
                  >
                    Get My Member Code — Join PopCrown
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PopCrown Community Spotlight — The MJ Experience */}
      <section className="py-20 relative overflow-hidden" style={{
        background: 'linear-gradient(180deg, #2d1a1a 0%, #1a1a1a 60%, #0f0f0f 100%)'
      }}>
        {/* Subtle gold dust overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 15% 20%, #E10600 0%, transparent 35%), radial-gradient(circle at 85% 75%, #DC143C 0%, transparent 40%)'
        }} />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-5 py-2 bg-[#E10600] text-black rounded-full text-sm font-bold uppercase tracking-wider mb-5" data-testid="community-spotlight-badge">
                <Star className="w-4 h-4" />
                Community Spotlight — Featured Member
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">
                <span className="thriller-text">The MJ Experience</span>
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                One of our newest PopCrown members — bringing Michael Jackson fans together through immersive events, creative workshops, themed art nights and exclusive merch.
              </p>
              <div className="mx-auto mt-5 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
            </div>

            {/* Brand card with hero image */}
            <div className="rounded-2xl overflow-hidden mb-10" style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
              border: '3px solid #E10600',
              boxShadow: '0 0 50px rgba(225, 6, 0, 0.25)'
            }} data-testid="mjexperience-brand-card">
              <div className="grid md:grid-cols-5 gap-0">
                <div className="md:col-span-2 relative min-h-[260px]">
                  <img
                    src="https://static.wixstatic.com/media/34f369_7fa6fa45490340ed84ada12a844066f2~mv2.jpg/v1/fill/w_1380,h_467,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/The%20MJ%20Experience%20Sip%20and%20Paint%20_edited.jpg"
                    alt="The MJ Experience Sip and Paint Michael Jackson themed art event London - PopCrown featured member"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="md:col-span-3 p-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-black mb-2">
                    Immersive MJ events. London-based. Fan-built.
                  </h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    The MJ Experience hosts Michael Jackson themed Sip &amp; Paint nights, hands-on Rhinestone Glove workshops and creative meet-ups for fans who want more than just music — they want to <em>make</em> something. Bring your own booze, bring a friend, leave with art, a glove, or both.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://www.themjexperience.co.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:scale-105"
                      style={{ background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)', color: '#fff' }}
                      data-testid="mjexperience-website-btn"
                    >
                      Visit themjexperience.co.uk
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <a
                      href="https://the-mj-experience.designmynight.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-black text-[#E10600] border-2 border-[#E10600] transition-all hover:bg-[#E10600] hover:text-black"
                      data-testid="mjexperience-allevents-btn"
                    >
                      See all upcoming events
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Three upcoming event cards */}
            <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">
              Upcoming MJ Experience Events
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mjexperience-events-grid">
              {upcomingEvents.length === 0 ? (
                <p className="col-span-2 text-center text-gray-300 py-10">
                  No upcoming events scheduled right now — check back soon. New dates land here as they&apos;re confirmed.
                </p>
              ) : (
                upcomingEvents.map((ev) => {
                  const badgeStyle =
                    ev.categoryColor === 'gold'
                      ? 'bg-[#E10600] text-black'
                      : 'bg-[#DC143C] text-white';
                  return (
                    <article
                      key={ev.id}
                      className="rounded-2xl overflow-hidden flex flex-col"
                      style={{
                        background: '#FFFFFF',
                        border: '2px solid #E10600',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                      }}
                      data-testid={`mjexperience-event-${ev.testIdSuffix}`}
                    >
                      <div className="h-44 relative">
                        <img
                          src={ev.image}
                          alt={ev.imageAlt}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <span className={`absolute top-3 left-3 px-3 py-1 ${badgeStyle} rounded-full text-xs font-bold uppercase`}>
                          {ev.categoryLabel}
                        </span>
                      </div>
                      <div className="p-5 flex flex-col flex-grow">
                        <div className="flex items-center gap-2 text-[#DC143C] font-bold text-sm mb-1">
                          <Calendar className="w-4 h-4" /> {ev.displayDate}
                        </div>
                        <h4 className="text-lg font-bold text-black mb-2">{ev.title}</h4>
                        <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
                          <MapPin className="w-4 h-4" /> {ev.city} &nbsp;·&nbsp; {ev.venueNote}
                        </div>
                        <p className="text-sm text-gray-600 mb-4 flex-grow">{ev.description}</p>
                        <a
                          href={ev.bookingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full font-bold text-white transition-all hover:scale-[1.02]"
                          style={{ background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)' }}
                          data-testid={`mjexperience-book-${ev.testIdSuffix}`}
                        >
                          <Ticket className="w-4 h-4" /> Book a Ticket <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </article>
                  );
                })
              )}
            </div>

            <p className="text-center text-sm text-gray-400 mt-8">
              The MJ Experience is an independent fan-led organisation. Events hosted and managed by The MJ Experience —{' '}
              <a
                href="https://www.themjexperience.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E10600] hover:underline"
              >
                themjexperience.co.uk
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* More Events Coming Section */}
      <section className="py-20" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="p-12 text-center rounded-xl" style={{
              background: '#FFFFFF',
              border: '2px solid #E5E7EB',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <div className="text-[#E10600] mb-6">
                <Star className="w-20 h-20 mx-auto" />
              </div>
              <h2 className="text-4xl font-bold text-black mb-4">
                More Events Coming Soon
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We're curating more tribute concerts, fan meetups, and special celebrations worldwide.
              </p>
              <div className="space-y-4 text-left max-w-2xl mx-auto">
                <div className="flex items-start gap-3 text-gray-700">
                  <MapPin className="w-5 h-5 text-[#E10600] mt-1 flex-shrink-0" />
                  <span><strong>Global Events:</strong> Find tribute concerts and fan gatherings in your city</span>
                </div>
                <div className="flex items-start gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-[#E10600] mt-1 flex-shrink-0" />
                  <span><strong>Special Dates:</strong> Celebrate milestone anniversaries and special occasions</span>
                </div>
                <div className="flex items-start gap-3 text-gray-700">
                  <Ticket className="w-5 h-5 text-[#E10600] mt-1 flex-shrink-0" />
                  <span><strong>Exclusive Access:</strong> Early bird tickets and VIP experiences for PopCrown members</span>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-gray-500">
                  Want to be notified when events go live? Join our newsletter!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Placeholder Events Info */}
      <section className="py-20 relative" style={{
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 50%, #FFF5F5 100%)'
      }}>
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, #E10600 0%, transparent 50%), radial-gradient(circle at 75% 75%, #DC143C 0%, transparent 50%)'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              What to <span className="thriller-text">Expect</span>
            </h2>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="flex">
              <div className="p-6 rounded-xl text-center flex flex-col w-full" style={{
                background: '#FFFFFF',
                border: '2px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                minHeight: '280px'
              }}>
                <Calendar className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-3">Tribute Concerts</h3>
                <p className="text-gray-600 flex-grow">
                  Experience MJ's music performed live by talented tribute artists worldwide
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="p-6 rounded-xl text-center flex flex-col w-full" style={{
                background: '#FFFFFF',
                border: '2px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                minHeight: '280px'
              }}>
                <MapPin className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-3">Fan Meetups</h3>
                <p className="text-gray-600 flex-grow">
                  Connect with fellow fans at organized meetups in major cities
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="p-6 rounded-xl text-center flex flex-col w-full" style={{
                background: '#FFFFFF',
                border: '2px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                minHeight: '280px'
              }}>
                <ExternalLink className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-3">Special Celebrations</h3>
                <p className="text-gray-600 flex-grow">
                  Celebrate MJ's legacy on special anniversaries and milestone dates
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;